import { createContext, useContext, useCallback, useMemo, useState } from "react";
import * as v0 from "@aidkitorg/types/lib/survey";
import { type Translations } from "@aidkitorg/i18n/lib"
import { useLocalizedStrings, useLocalizer } from "../../../Localization";
import { type ConnectionModalState } from "./types";

type ConnectionModalDistroStrings = NonNullable<v0.DeckVerification["connectionModalText"]>;
type ConnectionModalDefaultStrings = Translations["questions"]["deck_verification"]["connection_modal"]

interface ConnectionModalContext {
  token: string;
  targetField: string;
  state: ConnectionModalState;
  getLocalizedString: (
    richTextKey: keyof ConnectionModalDistroStrings,
    fallbackTextKey?: keyof ConnectionModalDefaultStrings,
  ) => string;
  onUpdateState: (newState: ConnectionModalState) => void;
  onConnectionSuccess: (connectionId: string) => void;
  onExit: () => void;
}

const ConnectionModalContext = createContext<ConnectionModalContext | null>(null);

export function useConnectionModal() {
  const context = useContext(ConnectionModalContext)
  if (!context) {
    throw new Error("useConnectionModal must be within a ConnectionModalProvider.")
  }

  return context;
}

interface ConnectionModalProviderProps {
  children: React.ReactNode;
  component: v0.DeckVerification;
  token: string;
  onSubmit: (connectionToken: string) => void;
  onExit: () => void;
}

export function ConnectionModalProvider({
  children,
  component,
  token,
  onSubmit,
  onExit,
}: ConnectionModalProviderProps) {
  const L = useLocalizedStrings();
  const localizer = useLocalizer();

  const [state, setState] = useState<ConnectionModalState>({ stage: "search" });

  const onConnectionSuccess = useCallback((connectionId: string) => {
    onSubmit(connectionId);
  }, [onSubmit]);

  const getLocalizedString = useCallback((
    richTextKey: keyof ConnectionModalDistroStrings,
    fallbackTextKey?: keyof ConnectionModalDefaultStrings,
  ) => {
    const distroText = localizer(component.connectionModalText?.[richTextKey]);
    const defaultText = fallbackTextKey ? L.questions.deck_verification.connection_modal[fallbackTextKey] : "";
    return distroText || defaultText;
  }, [localizer, component, L]);

  const contextValue = useMemo(() => ({
    token,
    targetField: component.targetField,
    state,
    getLocalizedString,
    onUpdateState: setState,
    onConnectionSuccess,
    onExit,
  }), [
    token,
    component,
    state,
    getLocalizedString,
    onConnectionSuccess,
    onExit,
  ]);

  return <ConnectionModalContext.Provider value={contextValue}>
    {children}
  </ConnectionModalContext.Provider>
}
